import type { GalleryItem } from "@/services/api/gallery";
import type { FileItem, FileType, Thumbnail, ImageDimensions } from "../types";

function mapThumbnail(
  thumbnail: GalleryItem["smallThumbnail"]
): Thumbnail | undefined {
  if (!thumbnail) return undefined;
  return {
    url: thumbnail.url,
    width: thumbnail.width ?? null,
    height: thumbnail.height ?? null,
  };
}

function mapImageDimensions(
  imageDimensions: GalleryItem["imageDimensions"]
): ImageDimensions | undefined {
  if (
    !imageDimensions ||
    imageDimensions.width === undefined ||
    imageDimensions.height === undefined
  ) {
    return undefined;
  }
  return {
    width: imageDimensions.width,
    height: imageDimensions.height,
  };
}

export function mapGalleryItemToFileItem(galleryItem: GalleryItem): FileItem {
  const extension = galleryItem.extension.toLowerCase();
  const contentType = galleryItem.contentType?.toLowerCase() || "";

  let fileType: FileType = "document";
  if (
    extension.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i) ||
    contentType.startsWith("image/")
  ) {
    fileType = "image";
  } else if (
    extension.match(/\.(mp4|avi|mov|wmv|flv|webm|mkv)$/i) ||
    contentType.startsWith("video/")
  ) {
    fileType = "video";
  } else if (
    extension.match(/\.(ppt|pptx)$/i) ||
    contentType.includes("presentation")
  ) {
    fileType = "presentation";
  } else if (
    extension.match(/\.(xls|xlsx|csv)$/i) ||
    contentType.includes("spreadsheet") ||
    contentType.includes("excel")
  ) {
    fileType = "spreadsheet";
  }

  const fileSizeInKB = galleryItem.fileSize / 1024;
  const fileSizeInMB = fileSizeInKB / 1024;
  const size =
    fileSizeInMB >= 1
      ? `${fileSizeInMB.toFixed(2)} MB`
      : `${fileSizeInKB.toFixed(2)} KB`;

  const tags: string[] = galleryItem.tags.map((tag) =>
    typeof tag === "string" ? tag : tag.name
  );

  const attributeValues = galleryItem.attributeValues.reduce(
    (acc, attr) => {
      acc[attr.attributeName] = attr.value;
      return acc;
    },
    {} as Record<string, string>
  );

  const smallThumbnailUrl = galleryItem.smallThumbnail?.url;
  const largeThumbnailUrl = galleryItem.largeThumbnail?.url;

  return {
    id: galleryItem.fileId,
    name: galleryItem.name,
    type: fileType,
    size,
    createdDate: galleryItem.createdDate,
    modifiedDate: galleryItem.modifiedDate,
    extension: galleryItem.extension,
    contentType: galleryItem.contentType || "",
    viewCount: galleryItem.viewCount,
    dataType: attributeValues["Data Type"],
    team: attributeValues["Team"],
    year: attributeValues["Year"]
      ? parseInt(attributeValues["Year"], 10)
      : undefined,
    event: attributeValues["Event"],
    tags,
    thumbnailUrl: largeThumbnailUrl || smallThumbnailUrl,
    thumbnailSmallUrl: smallThumbnailUrl,
    thumbnailLargeUrl: largeThumbnailUrl,
    imageDimensions: mapImageDimensions(galleryItem.imageDimensions),
    smallThumbnail: mapThumbnail(galleryItem.smallThumbnail),
    largeThumbnail: mapThumbnail(galleryItem.largeThumbnail),
    attributeValues: galleryItem.attributeValues,
    comments: galleryItem.comments,
    classifications: galleryItem.classifications,
  };
}
